import React, { InputHTMLAttributes, forwardRef } from "react";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import Button from "./Button";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  required?: boolean;
  convertPlaceholder?: boolean;
  TopRightElement?: React.ReactNode;
  isInteger?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      className,
      required,
      TopRightElement,
      convertPlaceholder = false,
      isInteger = false,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isInteger && props.onChange) {
        // Remove all non-integer characters and keep only digits
        const cleanedValue = e.target.value.replace(/[^0-9]/g, "");
        // Create a new event with the cleaned value
        const cleanedEvent = {
          ...e,
          target: {
            ...e.target,
            value: cleanedValue,
          },
        };
        props.onChange(cleanedEvent);
      } else if (props.onChange) {
        props.onChange(e);
      }
    };

    return (
      <div className={`${className ? className : ""}`}>
        {(label || TopRightElement) && (
          <div className="flex justify-between items-center mb-2">
            {label && (
              <label htmlFor={props.id} className="font-bold">
                {label}
                {required && <span className="text-red-500"> *</span>}
              </label>
            )}
            {TopRightElement}
          </div>
        )}
        <div className="relative w-full">
          <input
            ref={ref}
            className="w-full h-10 px-4 py-2 pr-10 border border-[#0F172A] rounded-md shadow-lg focus:outline-none focus:border-[#00A3E0] disabled:cursor-not-allowed"
            {...props}
            onChange={handleChange}
            type={isInteger ? "text" : props.type}
          />
          {convertPlaceholder &&
            props.value === "" &&
            props.placeholder &&
            props.placeholder.trim() !== "" && (
              <Button.Circle
                variant="custom"
                className="absolute top-1 right-2 p-1 text-gray-500 bg-white shadow-md hover:bg-gray-100 focus:outline-none"
                onClick={() => {
                  if (props.onChange) {
                    if (isInteger) {
                      const cleanedValue = props.placeholder.replace(
                        /[^0-9]/g,
                        "",
                      );
                      props.onChange({
                        target: { value: cleanedValue },
                      } as React.ChangeEvent<HTMLInputElement>);
                      return;
                    }
                    props.onChange({
                      target: { value: props.placeholder },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
              >
                <PencilSquareIcon className="w-5 h-5 text-electrum-200" />
              </Button.Circle>
            )}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
